<script>
import {useRoute, useRouter} from "vue-router";
import {useUserStore} from "../../../stores/user";
import Icon from "../../components/Icon.vue";
import DonationCard from "../components/DonationCard.vue";
import LoadingSpinner from "../../components/loadingSpinner.vue";
import {useLandingPageStore} from "@/javascript/stores/landingPage";
import DataCard from "../../components/overview/DataCard.vue";


export default {
  name: "DonorProfile",
  components: {LoadingSpinner, DonationCard, Icon, DataCard},
  emits: ['show-popup', 'switch-tab'],
  setup() {

    const router = useRouter();
    const route = useRoute();

    const userStore = useUserStore();
    const landingPageStore = useLandingPageStore();

    return {
      route,
      router,
      userStore,
      landingPageStore,
    };
  },
  data() {
    return {
      editing: false,
      saving: false
    };
  },
  computed: {

    user() {
      return this.userStore.user;
    },
    // Returns the latest 5 successful donations
    latestDonations() {
      return this.userStore.donations
        .filter(donation => donation.status === 'completed')
        .slice(0, 5);
    },

    userInitials() {
      if (!this.user) return "";
      const firstInitial = this.user.first_name?.trim()?.charAt(0)?.toUpperCase() || '';
      const lastInitial = this.user.last_name?.trim()?.charAt(0)?.toUpperCase() || '';
      return firstInitial + lastInitial;
    },

    // Returns a random colour for the user's avatar based on UUID;
    // Deterministic so it's always the same for one user but changes depending on who is logged in
    getColorForUser() {
      if (!this.user) return '';
      const colors = ['#08602d', '#044d3a', '#600823', '#082440', '#3a4473', 'purple'];

      // Simple hash function


      const uuid = this.user.uuid;
      let hash = 0;

      for (let i = 0; i < uuid.length; i++) {
        hash = (hash << 5) - hash + uuid.charCodeAt(i);
        hash |= 0; // Convert to 32bit integer
      }

      const index = Math.abs(hash) % colors.length;
      return colors[index];
    },
    fullName() {
      const f = this.user?.first_name?.trim() || "";
      const l = this.user?.last_name?.trim() || "";
      return `${f} ${l}`.trim();
    },
    today() {
      return new Date().toISOString().slice(0, 10);
    }
  },

  mounted() {

  },

  methods: {
    toggleEdit() {
      if (this.saving) return;

      if (this.editing) {
        this.saving = true;
        this.saveUser();
      }
      this.editing = !this.editing;
    },
    saveUser() {
      this.userStore.save().then(() => {
        this.$emit('show-popup', 'Profile Saved!');
        this.saving = false;
      });
    },

  }
};
</script>

<template>
  <div class="page-container h-full">
    <div class="inner-container h-full">
      <div class="flex flex-col gap-6 max-[639px]:gap-4 h-full">
        <data-card
          :loaded="!!user"
          title=""
          :left-sidebar="false"
          bordered
          border-class="border border-slate-200 rounded-lg shadow h-full"
        >
          <!-- Avatar -->
          <div class="flex items-center justify-center mx-auto mb-4">
            <div
              class="flex items-center justify-center w-20 h-20 rounded-full text-2xl font-semibold"
              style="
                  background-color: var(--brand-primary, #10be5a);
                  color: var(--brand-primary-text, #EBEBEB);
                  border: var(--brand-primary-border, 1px solid transparent);
                "
            >
              {{ userInitials }}
            </div>
          </div>

          <!-- Name & DOB (Editing) -->
          <div
            v-if="editing"
            class="flex flex-col items-center gap-3"
          >
            <div class="flex gap-3 max-w-xs w-full">
              <div class="flex-1">
                <label class="text-xs text-start text-gray-600 block mb-1">First Name</label>
                <input
                  v-model="user.first_name"
                  type="text"
                  class="input w-full"
                  @keyup.enter="toggleEdit"
                >
              </div>
              <div class="flex-1">
                <label class="text-xs text-start text-gray-600 block mb-1">Last Name</label>
                <input
                  v-model="user.last_name"
                  type="text"
                  class="input w-full"
                  @keyup.enter="toggleEdit"
                >
              </div>
            </div>
            <div class="max-w-xs w-full">
              <label class="text-xs text-start text-gray-600 block mb-1">Date of Birth</label>
              <input
                v-model="user.date_of_birth"
                type="date"
                :max="today"
                class="input w-full"
                @keyup.enter="toggleEdit"
              >
            </div>
          </div>
          <div
            v-else
            class="text-lg font-semibold text-gray-900 text-center"
          >
            {{ fullName || '—' }}
          </div>

          <!-- Email under the name (hidden while editing) -->
          <div
            v-if="!editing"
            class="text-gray-500 text-sm mt-1 break-words text-center"
          >
            {{ user?.email }}
          </div>

          <!-- Date of Birth (view) -->
          <div
            v-if="!editing"
            class="text-gray-500 text-sm mt-1 break-words text-center"
          >
            Date of Birth: {{ user?.date_of_birth || '—' }}
          </div>

          <!-- Divider -->
          <hr class="my-4 border-gray-200">

          <!-- Edit Button -->
          <div class="text-center">
            <loading-spinner
              v-if="saving"
              size="icon-large"
            />
            <button
              v-else
              type="button"
              class="btn-primary mt-1"
              @click="toggleEdit"
            >
              {{ editing ? 'Save Profile' : 'Edit Profile' }}
            </button>
          </div>
        </data-card>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

.page-container {
  @apply rounded-lg;
}

.inner-container {

  .donor-details {

    .user-field {
      @apply font-semibold rounded-lg mb-2 w-full break-words;
      padding: 4px;
      word-break: break-word;
      overflow-wrap: anywhere;

      input {
        box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
        border: 1px solid grey;
        border-radius: 4px;
        height: 24px;
        padding: 16px 12px;
      }
    }
  }

  .input {
    @apply rounded-md border border-gray-300 h-8 px-3 text-sm;
    @apply bg-gray-100;
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.06);

    &:focus {
      @apply outline-none bg-gray-100 border-gray-400;
    }
  }

  .btn-primary {
    @apply inline-flex items-center justify-center rounded-md px-4 py-2 font-semibold text-sm;
    background: var(--brand-primary, #10be5a);
    color: var(--brand-primary-text, #EBEBEB); /* ← auto-contrast from landing store */
    border: var(--brand-primary-border, 1px solid transparent);
    transition: background-color 0.15s ease;
  }

  .btn-primary:hover {
    /* darken only the background; text color stays from the var */
    background: color-mix(in srgb, var(--brand-primary, #10be5a) 85%, black);
  }

  .btn-primary:active {
    background: color-mix(in srgb, var(--brand-primary, #10be5a) 70%, black);
  }

}

</style>
